import styled from 'styled-components';
import type {
  AlignmentValue,
  PositionValue
} from '@elfsight/configurator-contracts';
import type { ButtonProps, FloatingButtonProps } from '@elfsight/widget-ui';
import { Button, FloatingButton } from '@elfsight/widget-ui';
import { useViewportWidth } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { isInstagram } from '../../constants';
import type { ChatsPosition } from '../../constants/types';
import { useChat } from '../chat';
import { getDefaultBubbleIcon } from '../helpers';
import { useSpacingOffset } from '../helpers/useSpacingOffset';
import { useBubbleAnimation } from './use-bubble-animation';

const BUBBLE_MIN_WIDTH = 64;

// for a standalone version
// export type ChatBubbleProps = {
//   portalContainerElement: Element;
//   bubbleBackgroundColor: string;
//   bubbleIconColor: string;
//   bubbleText: string;
//   bubbleIcon: any;
//   position: ChatsPosition;
//   bubbleAnimationEnabled: boolean;
//   bubbleAnimationInterval: number;
//   slideAnimation?: boolean;
//   align: AlignmentValue;
//   renderThumbnailMode?: boolean;
//   verticalOffset: number;
//   horizontalOffset: number;
//   notificationBadgeColor: string;
//   notifications: ChatsNotification[];
//   font: FontValue;
//   chatOpen: boolean;
//   toggleChatOpen: () => void;
// };

export const Bubble = ({
  portalContainerElement
}: {
  portalContainerElement: Element;
}) => {
  const {
    bubbleBackgroundColor,
    bubbleIconColor,
    bubbleText,
    bubbleIcon,
    position,
    bubbleAnimationEnabled,
    bubbleAnimationInterval,
    align,
    renderThumbnailMode,
    font,
    notificationBadgeColor,
    notifications
    // displayDeactivation
  } = useSettings();

  const { verticalOffset, horizontalOffset } = useSpacingOffset();
  const slideAnimation = isInstagram && position === 'floating';
  const { openChatWindow, handleToggleWindow } = useChat();

  const viewPortWidth = useViewportWidth(true);
  const bubbleMaxWidth = viewPortWidth - 2 * horizontalOffset;
  const currentBubbleMaxWidth = Math.max(BUBBLE_MIN_WIDTH, bubbleMaxWidth);

  const animationActive = useBubbleAnimation(
    bubbleAnimationEnabled,
    openChatWindow,
    bubbleAnimationInterval
  );

  const floatingBubblePosition = ALIGN_POSITION_MAP[align];
  const ariaLabel = bubbleText
    ? undefined
    : `${openChatWindow ? 'Close' : 'Open'} chat window`;

  const animateColor =
    bubbleBackgroundColor === 'rgb(255, 255, 255)'
      ? bubbleIconColor
      : bubbleBackgroundColor;

  const showBubbleRedBadge = notifications.includes('bubbleBadge');

  // @TODO type properly FloatingButtonProps or ButtonProps
  const buttonProps: ButtonProps | FloatingButtonProps = {
    role: 'button',
    tabIndex: 0,
    color: bubbleBackgroundColor,
    borderRadius: 100,
    borderWidth: 0,
    fontColor: bubbleIconColor,
    font: {
      fontSize: bubbleText ? 16 : 28,
      fontWeight: 'bold'
    },
    horizontalPadding: bubbleText ? 25 : 16,
    verticalPadding: bubbleText ? 10 : 16,
    iconOnly: !bubbleText,
    iconLeft: bubbleIcon || getDefaultBubbleIcon(),
    style: {
      maxWidth: currentBubbleMaxWidth,
      padding: 0
    },
    onClick: handleToggleWindow,
    'aria-label': ariaLabel
  };

  if (position === 'floating') {
    (buttonProps as FloatingButtonProps).position = {
      position: floatingBubblePosition,
      verticalOffset: verticalOffset || 1,
      horizontalOffset: horizontalOffset || 1
    };
    (buttonProps as FloatingButtonProps).withShadow = false;
    (buttonProps as FloatingButtonProps).containerStyle = {
      maxWidth: currentBubbleMaxWidth
    };
    (buttonProps as FloatingButtonProps).portalContainerElement =
      portalContainerElement;
  }

  return (
    <BubbleComponent
      {...buttonProps}
      bubbleType={position}
      _showBubble={openChatWindow}
      _animate={animationActive}
      _animateColor={animateColor}
      _slideAnimation={slideAnimation}
      _thumbnailMode={renderThumbnailMode}
    >
      <NotificationBadge
        _showBadge={showBubbleRedBadge}
        _badgeColor={notificationBadgeColor}
      />
      <BubbleContentText _font={font?.value}>{bubbleText}</BubbleContentText>
    </BubbleComponent>
  );
};

const ALIGN_POSITION_MAP: Record<AlignmentValue, PositionValue> = {
  right: 'bottom-right',
  center: 'bottom',
  left: 'bottom-left'
};

const animationWithSlide = (
  _slideAnimation?: boolean,
  _showBubble?: boolean,
  _thumbnailMode?: boolean
) =>
  _slideAnimation &&
  `
  transform: ${_showBubble ? 'translateY(150%)' : 'translateY(0)'};
  opacity: ${_showBubble ? 0 : 1};
  transition: ${_thumbnailMode ? 'none' : 'opacity 0.2s, transform 0.3s'};
`;

const pulseAnimation = (_animate: boolean) =>
  _animate &&
  `
    &::before,
    &::after {
      animation: es-chats-pulse 1s linear infinite;
    }

    &::after {
      animation-delay: 0.5s;
    }

    @keyframes es-chats-pulse {
      0% {
        transform: scale(1);
        opacity: 0;
      }

      50% {
        opacity: 1;
      }

      100% {
        transform: scale(1.2);
        opacity: 0;
      }
    }
  `;

type Animation = {
  _animateColor: string;
  _slideAnimation?: boolean;
  _showBubble: boolean;
  _animate: boolean;
  _thumbnailMode?: boolean;
};
const animation = ({
  _animateColor = '#fff',
  _slideAnimation,
  _showBubble,
  _animate,
  _thumbnailMode
}: Animation) => `
  position: relative;

  &:before,
  &:after {
    content: '';
    position: absolute;
    border: 1px solid ${_animateColor};
    left: -2px;
    right: -2px;
    top: -2px;
    bottom: -2px;
    border-radius: 500px;
    opacity: 0;
    z-index: 0;
  }

  ${animationWithSlide(_slideAnimation, _showBubble, _thumbnailMode) || ''}
  ${pulseAnimation(_animate) || ''}
`;

type BubbleButtonProps = {
  bubbleType: ChatsPosition;
} & (ButtonProps | FloatingButtonProps);

export const BubbleButton = ({
  bubbleType,
  ...forwardingProps
}: BubbleButtonProps) => {
  return bubbleType === 'floating' ? (
    <FloatingButton {...(forwardingProps as FloatingButtonProps)} />
  ) : (
    <Button {...(forwardingProps as ButtonProps)} />
  );
};

const BubbleComponent = styled(BubbleButton)<Animation>`
  box-shadow: ${({ _thumbnailMode }) =>
    _thumbnailMode
      ? '0 6px 30px 0 rgba(0, 0, 0, 0.1)'
      : '0 3px 12px 0 rgba(0, 0, 0, 0.15)'};

  ${animation}
  html[dir='rtl'] && span > div {
    margin-left: ${({ iconOnly }) => !iconOnly && '8px'};
    margin-right: ${({ iconOnly }) => !iconOnly && '-4px'};
  }
`;

const NotificationBadge = styled.div<{
  _showBadge: boolean;
  _badgeColor: string;
}>`
  width: 10px;
  height: 10px;
  background-color: ${({ _badgeColor }) => _badgeColor};
  display: ${({ _showBadge }) => (_showBadge ? 'block' : 'none')} !important;
  position: absolute;
  z-index: 1;
  border-radius: 50%;
  right: 4px;
  top: 4px;
`;

const BubbleContentText = styled.span<{ _font?: string }>`
  ${({ _font }) =>
    _font &&
    `
      font-family: ${_font};
    `}
`;
